import { Injectable } from '@angular/core';
import { PlatformApiEndPoints } from '../endpoints';
import { ResourceBaseService } from '../../resource-base.service';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import environment from '../../../../../environments/environment';
import { HttpUtilityService } from '../../http-utility/http-utility.service';
import { Observable, of, throwError } from 'rxjs';
import { IPlatformSession, IPlatformAuthenticateRequest, IPlatformAuthenticateResult, IUserDomain } from '../../../model/platform-service/platform-service-models';
import { map, catchError } from 'rxjs/operators';
import { StringUtilities } from '@app/core/utilities/string-utilities';
import { ConsoleLogger } from '@app/core/utilities/consolelogger';
import { isNullOrUndefined } from 'util';
import _ from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { Constants } from '@app/core/utilities/constants';

export const PLATFORM_EULA_ERROR_CODE = 'PS_AUTH_065';

export interface IPlatformSessionService {
  validateSessionToken(psToken: string): Observable<IPlatformSession>;
  invalidateSessionToken(psToken: string): Observable<boolean>;
  authenticate(psToken: string): Observable<IPlatformAuthenticateResult>;
}

@Injectable({
  providedIn: 'root'
})
export class PlatformSessionService extends ResourceBaseService<IPlatformSession> implements IPlatformSessionService {

  constructor(private httpClient: HttpClient,
              private httpUtilityService: HttpUtilityService,
              private cookieService: CookieService) {
    super(httpClient, PlatformApiEndPoints.session);
  }

  /**
   * Validates whether the Pstoken is valid
   * @param psToken The Ps Token
   */
  validateSessionToken(psToken: string): Observable<IPlatformSession> {
    return this.post('ValidateToken/', null, this.getHttpOptionsWithPsToken(psToken));
  }

  /**
   * Invalidates the session token (psToken)
   * @param psToken Platform session token
   */
  invalidateSessionToken(psToken: string) {
    return this.delete('InvalidateToken/', this.getHttpOptionsWithPsToken(psToken)).pipe(
      map(response => true),
      catchError(response => throwError(response))
    );
  }

  /**
   * Authenticates the user session via Platform's auth/authenticate endpoint.
   * Sends the psToken cookie automatically via withCredentials.
   * Detects EULA-not-accepted (PS_AUTH_065) and returns the acceptance URL.
   * @param psToken The platform session token, used to extract the user's domain
   */
  authenticate(psToken: string): Observable<IPlatformAuthenticateResult> {
    const domain = this.httpUtilityService.getDomainFromPsToken(psToken);
    const resolvedDomain = !isNullOrUndefined(domain) ? domain : this.httpUtilityService.getDomainFromCurrentRequestUrl();

    const rURL = this.httpUtilityService.buildDomainBaseUrl(resolvedDomain);
    const pathAndQuery = this.httpUtilityService.getPathAndQueryFromCurrentRequestUrl();
    const fullReturnUrl = pathAndQuery ? `${rURL}/${pathAndQuery}` : rURL;
    const clientVarsBase64 = StringUtilities.toBase64String(fullReturnUrl).replace('+', '-').replace('/', '_');
    const clientVars = _.trimEnd(clientVarsBase64, '=');

    const body: IPlatformAuthenticateRequest = {
      rURL,
      clientVars,
      redirectMethod: 'GET',
      clientId: environment.clientId,
      domain: resolvedDomain
    };

    return this.httpClient.post<any>(PlatformApiEndPoints.authenticate, body, {
      withCredentials: true,
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    }).pipe(
      map(() => {
        return { authenticated: true, eulaRequired: false, eulaAcceptanceUrl: null };
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400 && error.error && error.error.errorCode === PLATFORM_EULA_ERROR_CODE) {
          const eulaUrl = error.error['eula-acceptance-url'];
          ConsoleLogger.logError('User EULA not accepted: ' + JSON.stringify(error.error));
          return of({ authenticated: false, eulaRequired: true, eulaAcceptanceUrl: eulaUrl });
        }
        return of({ authenticated: false, eulaRequired: false, eulaAcceptanceUrl: null });
      })
    );
  }

  // Gets the list of domains the user belongs to. Used for populating domain dropdown in self-transfer flow.
  getUserDomains(username: string): Observable<IUserDomain[]> {
    const psToken = this.cookieService.get(Constants.Auth.authpsToken);
    return this.httpClient.post<IUserDomain[]>(PlatformApiEndPoints.userDomain, null, {
      params: { username },
      withCredentials: true,
      headers: new HttpHeaders({ 'Authorization': `Bearer ${psToken}` })
    });
  }

  /**
   * Gets the Http options to be passed in the request
   * @param psToken Ps token
   */
  // tslint:disable-next-line: ban-types
  private getHttpOptionsWithPsToken(psToken: string): Object {
    const domain = this.httpUtilityService.getDomainFromPsToken(psToken);
    return {
      headers: new HttpHeaders({
        clientId: environment.clientId,
        access_token: psToken || "undefined",
        domain: !isNullOrUndefined(domain) ? domain : this.httpUtilityService.getDomainFromCurrentRequestUrl()
      })
    };
  }


}
